import {
    formatAsDecimal,
    formatAsHidden,
    formatAsIndustryText,
    formatAsIsOrNotAvailable,
    formatAsLocalizedDate,
    formatAsMoneyAmount,
    formatAsNumberWithDays,
    formatAsPercentage,
} from "@/common/format";

/**
 * Value formatter for localized dates
 * @param params Supports all params from @see {@link formatAsLocalizedDate}.
 */
function asLocalizedDate(params = {}) {
    return (cellParams) => {
        return formatAsLocalizedDate(cellParams.value, params);
    };
}

/**
 * Value formatter for money amounts
 * @param params Supports all params from @see {@link formatAsMoneyAmount}.
 *               Additionally a currency data field can be specified to set the currency.
 */
function asMoneyAmount(params = {}) {
    const defaults = {
        currencyField: "",
        percentValueField: "",
    };
    params = { ...defaults, ...params };

    return (cellParams) => {
        return formatAsMoneyAmount(cellParams.value, {
            ...params,

            // Instead of a fixed currency, the currency can be loaded from the data set if the data field is specified
            currency: params.currencyField ? cellParams.data[params.currencyField] : params.currency,

            // Instead of a fixed percent value, the percent value can be loaded from the data set if the data field is specified
            percentValue: params.percentValueField ? cellParams.data[params.percentValueField] : params.percent,
        });
    };
}

/**
 * Value formatter for decimals
 * @param params Supports all params from @see {@link formatAsDecimal}.
 */
function asDecimal(params = {}) {
    return (cellParams) => {
        return formatAsDecimal(cellParams.value, params);
    };
}

/**
 * Value formatter for localized hidden values
 * @param params Supports all params from @see {@link formatAsHidden}.
 */
function asHidden(params = {}) {
    return (cellParams) => {
        return formatAsHidden(cellParams.value, params);
    };
}

/**
 * Value formatter for localizing an industry id
 * @param params Supports all params from @see {@link formatAsIndustryText}.
 */
function asIndustryText(params = {}) {
    return (cellParams) => {
        return formatAsIndustryText(cellParams.value, params);
    };
}

/**
 * Value formatter for localizing an industry id
 * @param params Supports all params from @see {@link formatAsIsOrNotAvailable}.
 */
function asIsOrNotAvailable(params = {}) {
    return (cellParams) => {
        return formatAsIsOrNotAvailable(cellParams.value, params);
    };
}

/**
 * Value formatter for display value or n/a if a given cell param is 0
 * @param otherCellField
 */
function asIsOrNotAvailableIfOtherCellIsZero(otherCellField) {
    return (cellParams) => {
        const otherCellValue = cellParams.data[otherCellField];
        if (otherCellValue !== undefined && otherCellValue !== 0) {
            return formatAsIsOrNotAvailable(cellParams.value)
        } else {
            return formatAsIsOrNotAvailable(undefined)
        }
    };
}

/**
 * Value formatter for number with day/days behind
 * @param params Supports all params from @see {@link formatAsNumberWithDays}.
 */
function asLocalizedNumberWithDays(params = {}) {
    return (cellParams) => {
        return formatAsNumberWithDays(cellParams.value, params);
    };
}

/**
 * Value formatter for decimals as percentage
 * @param params Supports all params from @see {@link formatAsPercentage}.
 */
function asPercentage(params = {}) {
    return (cellParams) => {
        return formatAsPercentage(cellParams.value, params)
    };
}

export {
    asLocalizedDate,
    asMoneyAmount,
    asIndustryText,
    asDecimal,
    asHidden,
    asLocalizedNumberWithDays,
    asIsOrNotAvailable,
    asIsOrNotAvailableIfOtherCellIsZero,
    asPercentage
};